import type {
  ContentPersona,
  FeedbackVerbosity,
  PassageFontScale,
  SceneIllustrationMode,
  TutorVoice,
  VocabRevealMode,
} from "@prisma/client";
import { allOf } from "@lib/enumOptions";

export const DEFAULT_PASSAGE_FONT_SCALE: PassageFontScale = "MD";
export const DEFAULT_TUTOR_SPEECH_SPEED = 1;
export const DEFAULT_AUTOPLAY_TUTOR_AUDIO = true;
export const DEFAULT_PASSAGE_AUDIO_SPEED = 0.85;
export const DEFAULT_TUTOR_VOICE: TutorVoice = "NOVA";
export const DEFAULT_FEEDBACK_VERBOSITY: FeedbackVerbosity = "BRIEF";
export const DEFAULT_VOCAB_REVEAL_MODE: VocabRevealMode = "TAP";
export const DEFAULT_CONTENT_PERSONA: ContentPersona = "TEXTBOOK_AUTHOR";
export const DEFAULT_MAX_RECORDING_SECONDS = 120;
export const DEFAULT_REDUCED_MOTION = false;
export const DEFAULT_SCENE_ILLUSTRATION_MODE: SceneIllustrationMode = "IMAGE";

export const PASSAGE_FONT_SCALE_OPTIONS = allOf<PassageFontScale>()(["SM", "MD", "LG"] as const);

export const PASSAGE_FONT_SCALE_CLASSES: Record<PassageFontScale, string> = {
  SM: "text-[1.125rem] leading-[1.85]",
  MD: "text-[1.375rem] leading-[2]",
  LG: "text-[1.625rem] leading-[2.15]",
};

export const TUTOR_SPEECH_SPEED_MIN = 0.75;
export const TUTOR_SPEECH_SPEED_MAX = 1.25;
export const TUTOR_SPEECH_SPEED_OPTIONS = [0.75, 0.875, 1, 1.125, 1.25] as const;

export const PASSAGE_AUDIO_SPEED_MIN = 0.75;
export const PASSAGE_AUDIO_SPEED_MAX = 1.1;
export const PASSAGE_AUDIO_SPEED_OPTIONS = [0.75, 0.85, 0.95, 1, 1.1] as const;

/** Dictation uses native TTS speed (1.0) - sub-1.0 values time-stretch on tts-1 and sound echo-y. */
export const DICTATION_AUDIO_SPEED_MIN = 0.75;
/** Native-speed TTS for dictation "Listen". */
export const DICTATION_LISTEN_TTS_SPEED = 1;
/** "Listen slowly" slows the normal clip client-side with pitch preserved. */
export const DICTATION_SLOW_PLAYBACK_RATE = 0.75;

export const TUTOR_VOICE_OPTIONS = allOf<TutorVoice>()([
  "NOVA",
  "SHIMMER",
  "ECHO",
  "ONYX",
  "ALLOY",
  "FABLE",
] as const);

export const FEEDBACK_VERBOSITY_OPTIONS = allOf<FeedbackVerbosity>()([
  "BRIEF",
  "DETAILED",
] as const);
export const VOCAB_REVEAL_MODE_OPTIONS = allOf<VocabRevealMode>()(["TAP", "ALWAYS"] as const);
export const CONTENT_PERSONA_OPTIONS = allOf<ContentPersona>()([
  "TEXTBOOK_AUTHOR",
  "NATIVE_SPEAKER",
] as const);
export const MAX_RECORDING_SECONDS_OPTIONS = [60, 90, 120, 180, 240] as const;
export const MAX_RECORDING_SECONDS_MIN = MAX_RECORDING_SECONDS_OPTIONS[0];
export const MAX_RECORDING_SECONDS_MAX =
  MAX_RECORDING_SECONDS_OPTIONS[MAX_RECORDING_SECONDS_OPTIONS.length - 1];
export const SCENE_ILLUSTRATION_MODE_OPTIONS = allOf<SceneIllustrationMode>()([
  "VIDEO",
  "IMAGE",
] as const);

export type LessonPreferences = {
  passageFontScale: PassageFontScale;
  tutorSpeechSpeed: number;
  autoplayTutorAudio: boolean;
  tutorVoice: TutorVoice;
  passageAudioSpeed: number;
  feedbackVerbosity: FeedbackVerbosity;
  vocabRevealMode: VocabRevealMode;
  contentPersona: ContentPersona;
  maxRecordingSeconds: number;
  reducedMotion: boolean;
  sceneIllustrationMode: SceneIllustrationMode;
};

export const DEFAULT_LESSON_PREFERENCES: LessonPreferences = {
  passageFontScale: DEFAULT_PASSAGE_FONT_SCALE,
  tutorSpeechSpeed: DEFAULT_TUTOR_SPEECH_SPEED,
  autoplayTutorAudio: DEFAULT_AUTOPLAY_TUTOR_AUDIO,
  tutorVoice: DEFAULT_TUTOR_VOICE,
  passageAudioSpeed: DEFAULT_PASSAGE_AUDIO_SPEED,
  feedbackVerbosity: DEFAULT_FEEDBACK_VERBOSITY,
  vocabRevealMode: DEFAULT_VOCAB_REVEAL_MODE,
  contentPersona: DEFAULT_CONTENT_PERSONA,
  maxRecordingSeconds: DEFAULT_MAX_RECORDING_SECONDS,
  reducedMotion: DEFAULT_REDUCED_MOTION,
  sceneIllustrationMode: DEFAULT_SCENE_ILLUSTRATION_MODE,
};

export function isSceneIllustrationVideoEnabled(
  prefs: Pick<LessonPreferences, "reducedMotion" | "sceneIllustrationMode">,
  sceneVideoAvailable = false,
): boolean {
  return sceneVideoAvailable && prefs.sceneIllustrationMode === "VIDEO" && !prefs.reducedMotion;
}

/** @deprecated Use LessonPreferences */
export type Slice1LessonPreferences = Pick<
  LessonPreferences,
  "passageFontScale" | "tutorSpeechSpeed" | "autoplayTutorAudio"
>;

export function tutorVoiceToOpenAI(voice: TutorVoice): string {
  return voice.toLowerCase();
}

export function clampTutorSpeechSpeed(value: number): number {
  return Math.min(TUTOR_SPEECH_SPEED_MAX, Math.max(TUTOR_SPEECH_SPEED_MIN, value));
}

export function clampPassageAudioSpeed(value: number): number {
  return Math.min(PASSAGE_AUDIO_SPEED_MAX, Math.max(PASSAGE_AUDIO_SPEED_MIN, value));
}

export function clampDictationAudioSpeed(value: number): number {
  return Math.min(PASSAGE_AUDIO_SPEED_MAX, Math.max(DICTATION_AUDIO_SPEED_MIN, value));
}

export function clampMaxRecordingSeconds(value: number): number {
  const clamped = Math.min(
    MAX_RECORDING_SECONDS_MAX,
    Math.max(MAX_RECORDING_SECONDS_MIN, Math.round(value)),
  );
  let nearest: number = MAX_RECORDING_SECONDS_OPTIONS[0];
  let nearestDistance = Math.abs(clamped - nearest);
  for (const option of MAX_RECORDING_SECONDS_OPTIONS) {
    const distance = Math.abs(clamped - option);
    if (distance < nearestDistance) {
      nearest = option;
      nearestDistance = distance;
    }
  }
  return nearest;
}

/** Maps stored tutor speed to browser speechSynthesis rate (1.0 pref ≈ prior 0.9 default). */
export function speechSynthesisRate(speed: number): number {
  return clampTutorSpeechSpeed(speed) * 0.9;
}

export type PassageAudioExpectation = {
  passage: string;
  speed: number;
  voice: string;
};

export function passageAudioExpectation(
  passage: string,
  prefs: Pick<LessonPreferences, "passageAudioSpeed" | "tutorVoice">,
): PassageAudioExpectation {
  return {
    passage,
    speed: clampPassageAudioSpeed(prefs.passageAudioSpeed),
    voice: tutorVoiceToOpenAI(prefs.tutorVoice),
  };
}

export function pickLessonPreferences(
  prefs: Partial<LessonPreferences> | null | undefined,
): LessonPreferences {
  return {
    passageFontScale: prefs?.passageFontScale ?? DEFAULT_PASSAGE_FONT_SCALE,
    tutorSpeechSpeed: clampTutorSpeechSpeed(prefs?.tutorSpeechSpeed ?? DEFAULT_TUTOR_SPEECH_SPEED),
    autoplayTutorAudio: prefs?.autoplayTutorAudio ?? DEFAULT_AUTOPLAY_TUTOR_AUDIO,
    tutorVoice: prefs?.tutorVoice ?? DEFAULT_TUTOR_VOICE,
    passageAudioSpeed: clampPassageAudioSpeed(
      prefs?.passageAudioSpeed ?? DEFAULT_PASSAGE_AUDIO_SPEED,
    ),
    feedbackVerbosity: prefs?.feedbackVerbosity ?? DEFAULT_FEEDBACK_VERBOSITY,
    vocabRevealMode: prefs?.vocabRevealMode ?? DEFAULT_VOCAB_REVEAL_MODE,
    contentPersona: prefs?.contentPersona ?? DEFAULT_CONTENT_PERSONA,
    maxRecordingSeconds: clampMaxRecordingSeconds(
      prefs?.maxRecordingSeconds ?? DEFAULT_MAX_RECORDING_SECONDS,
    ),
    reducedMotion: prefs?.reducedMotion ?? DEFAULT_REDUCED_MOTION,
    sceneIllustrationMode: prefs?.sceneIllustrationMode ?? DEFAULT_SCENE_ILLUSTRATION_MODE,
  };
}

/** @deprecated Use pickLessonPreferences */
export function pickSlice1Preferences(
  prefs: Partial<LessonPreferences> | null | undefined,
): Slice1LessonPreferences {
  const resolved = pickLessonPreferences(prefs);
  return {
    passageFontScale: resolved.passageFontScale,
    tutorSpeechSpeed: resolved.tutorSpeechSpeed,
    autoplayTutorAudio: resolved.autoplayTutorAudio,
  };
}
